import { HttpCoreService } from "../services/HttpCoreService";
import type { ApiResponse } from "../types/auth.type";
import type {
  CDARequest,
  CDATemplate,
  CDAListResponse,
  CDAStatistics,
  CreateCDARequestPayload,
  SignCDARequestPayload,
  RejectCDARequestPayload,
  ClarificationCDARequestPayload,
  DeleteCDARequestPayload,
  CreateCDATemplatePayload,
  UpdateCDATemplatePayload,
  CDARequestFilters,
  CDATemplateFilters,
  CDAAuditEntry,
  UploadManualCDAPayload,
  ManualCDAUploadResult,
} from "../types/cda.type";

// ─── Helper ───────────────────────────────────────────────────────────────────

function assertSuccess<T>(
  response: ApiResponse<T>,
  errorMsg: string,
): asserts response is ApiResponse<T> & { data: T } {
  const ok = response.statusCode >= 200 && response.statusCode < 300;
  if (!ok) {
    throw new Error(response.error ?? response.message ?? errorMsg);
  }
  if (!response.data) {
    throw new Error(`${errorMsg}: No data returned from server.`);
  }
}

// ─── Interface ────────────────────────────────────────────────────────────────

export interface ICDARepository {
  // CDA Request operations
  listRequests(
    filters?: CDARequestFilters,
  ): Promise<CDAListResponse<CDARequest>>;
  getRequestById(id: string): Promise<CDARequest>;
  createRequests(payload: CreateCDARequestPayload): Promise<CDARequest[]>;
  sendRequest(id: string): Promise<CDARequest>;
  resendRequest(id: string): Promise<CDARequest>;
  signRequest(id: string, payload: SignCDARequestPayload): Promise<CDARequest>;
  rejectRequest(
    id: string,
    payload: RejectCDARequestPayload,
  ): Promise<CDARequest>;
  requestClarification(
    id: string,
    payload: ClarificationCDARequestPayload,
  ): Promise<CDARequest>;
  cancelRequest(id: string): Promise<CDARequest>;
  deleteManualRequest(
    id: string,
    payload: DeleteCDARequestPayload,
  ): Promise<CDARequest>;
  fileToEtmf(id: string): Promise<CDARequest>;
  markViewed(id: string): Promise<CDARequest>;
  getAuditTrail(id: string): Promise<CDAAuditEntry[]>;

  /** PI email link — no JWT / Kong consumer (guarded CDA APIs will fail). */
  getPublicRequestById(id: string): Promise<CDARequest>;
  markViewedViaPublicLink(id: string): Promise<CDARequest>;
  signRequestViaPublicLink(
    id: string,
    payload: SignCDARequestPayload,
  ): Promise<CDARequest>;
  rejectRequestViaPublicLink(
    id: string,
    payload: RejectCDARequestPayload,
  ): Promise<CDARequest>;
  requestClarificationViaPublicLink(
    id: string,
    payload: ClarificationCDARequestPayload,
  ): Promise<CDARequest>;
  /** Fetch document as authenticated Blob (iframe-safe object URL). */
  getDocumentBlob(id: string): Promise<Blob>;
  /** Fetch the template content for a CDA request via the public (no-auth) endpoint. */
  getPublicTemplateByRequestId(
    requestId: string,
  ): Promise<{ id: string; name: string; content: string }>;

  // CDA Template operations
  listTemplates(
    filters?: CDATemplateFilters,
  ): Promise<CDAListResponse<CDATemplate>>;
  getTemplateById(id: string): Promise<CDATemplate>;
  createTemplate(payload: CreateCDATemplatePayload): Promise<CDATemplate>;
  updateTemplate(
    id: string,
    payload: UpdateCDATemplatePayload,
  ): Promise<CDATemplate>;
  activateTemplate(id: string): Promise<CDATemplate>;
  archiveTemplate(id: string): Promise<CDATemplate>;
  duplicateTemplate(id: string): Promise<CDATemplate>;

  // Statistics
  getStatistics(studyId?: string): Promise<CDAStatistics>;

  // Manual upload
  uploadManualCDA(
    payload: UploadManualCDAPayload,
  ): Promise<ManualCDAUploadResult>;
}

// ─── Implementation ───────────────────────────────────────────────────────────

export class CDARepository implements ICDARepository {
  private readonly httpCoreService: HttpCoreService;

  constructor(httpCoreService: HttpCoreService) {
    this.httpCoreService = httpCoreService;
  }

  private buildQueryString(
    params: Record<string, string | number | undefined>,
  ): string {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, val]) => {
      if (val !== undefined && val !== null && val !== "") {
        qs.set(key, String(val));
      }
    });
    const str = qs.toString();
    return str ? `?${str}` : "";
  }

  // ── CDA Requests ──────────────────────────────────────────────────────────

  async listRequests(
    filters?: CDARequestFilters,
  ): Promise<CDAListResponse<CDARequest>> {
    const url = `/cda/requests${this.buildQueryString({
      studyId: filters?.studyId,
      siteId: filters?.siteId,
      country: filters?.country,
      status: filters?.status,
      search: filters?.search,
      page: filters?.page ?? 1,
      limit: filters?.limit ?? 50,
    })}`;
    const response =
      await this.httpCoreService.get<CDAListResponse<CDARequest>>(url);
    assertSuccess(response, "Failed to fetch CDA requests");
    return response.data;
  }

  async getRequestById(id: string): Promise<CDARequest> {
    const response = await this.httpCoreService.get<CDARequest>(
      `/cda/requests/${id}`,
    );
    assertSuccess(response, "Failed to fetch CDA request");
    return response.data;
  }

  async createRequests(
    payload: CreateCDARequestPayload,
  ): Promise<CDARequest[]> {
    const response = await this.httpCoreService.post<
      CDARequest[],
      CreateCDARequestPayload
    >("/cda/requests", payload);
    assertSuccess(response, "Failed to create CDA requests");
    return response.data;
  }

  async sendRequest(id: string): Promise<CDARequest> {
    const response = await this.httpCoreService.post<CDARequest>(
      `/cda/requests/${id}/send`,
    );
    assertSuccess(response, "Failed to send CDA request");
    return response.data;
  }

  async resendRequest(id: string): Promise<CDARequest> {
    const response = await this.httpCoreService.post<CDARequest>(
      `/cda/requests/${id}/resend`,
    );
    assertSuccess(response, "Failed to resend CDA request");
    return response.data;
  }

  async signRequest(
    id: string,
    payload: SignCDARequestPayload,
  ): Promise<CDARequest> {
    const response = await this.httpCoreService.post<
      CDARequest,
      SignCDARequestPayload
    >(`/cda/requests/${id}/sign`, payload);
    assertSuccess(response, "Failed to sign CDA");
    return response.data;
  }

  async rejectRequest(
    id: string,
    payload: RejectCDARequestPayload,
  ): Promise<CDARequest> {
    const response = await this.httpCoreService.post<
      CDARequest,
      RejectCDARequestPayload
    >(`/cda/requests/${id}/reject`, payload);
    assertSuccess(response, "Failed to reject CDA");
    return response.data;
  }

  async requestClarification(
    id: string,
    payload: ClarificationCDARequestPayload,
  ): Promise<CDARequest> {
    const response = await this.httpCoreService.post<
      CDARequest,
      ClarificationCDARequestPayload
    >(`/cda/requests/${id}/clarification`, payload);
    assertSuccess(response, "Failed to request clarification");
    return response.data;
  }

  async cancelRequest(id: string): Promise<CDARequest> {
    const response = await this.httpCoreService.post<CDARequest>(
      `/cda/requests/${id}/cancel`,
    );
    assertSuccess(response, "Failed to cancel CDA request");
    return response.data;
  }

  async deleteManualRequest(
    id: string,
    payload: DeleteCDARequestPayload,
  ): Promise<CDARequest> {
    const response = await this.httpCoreService.post<CDARequest>(
      `/cda/requests/${id}/delete`,
      payload,
    );
    assertSuccess(response, "Failed to delete CDA request");
    return response.data;
  }

  async fileToEtmf(id: string): Promise<CDARequest> {
    const response = await this.httpCoreService.post<CDARequest>(
      `/cda/requests/${id}/file-to-etmf`,
    );
    assertSuccess(response, "Failed to file CDA to eTMF");
    return response.data;
  }

  async markViewed(id: string): Promise<CDARequest> {
    const response = await this.httpCoreService.post<CDARequest>(
      `/cda/requests/${id}/view`,
    );
    assertSuccess(response, "Failed to mark CDA as viewed");
    return response.data;
  }

  async getAuditTrail(id: string): Promise<CDAAuditEntry[]> {
    const response = await this.httpCoreService.get<CDAAuditEntry[]>(
      `/cda/requests/${id}/audit`,
    );
    assertSuccess(response, "Failed to fetch CDA audit trail");
    return response.data;
  }

  async getPublicRequestById(id: string): Promise<CDARequest> {
    try {
      const response = await this.httpCoreService.get<CDARequest>(
        `/cda/public/requests/${id}`,
      );
      assertSuccess(response, "Failed to fetch CDA request");
      return response.data;
    } catch (err: unknown) {
      const status =
        err &&
        typeof err === "object" &&
        "status" in err &&
        typeof err.status === "number"
          ? (err as { status: number }).status
          : undefined;
      // Only fall back to the authenticated endpoint when the public route
      // does not exist (older backend). For 401/403 the PI truly has no access.
      if (status === 404 || status === 405) {
        return this.getRequestById(id);
      }
      throw err;
    }
  }

  async markViewedViaPublicLink(id: string): Promise<CDARequest> {
    try {
      const response = await this.httpCoreService.post<CDARequest>(
        `/cda/public/requests/${id}/view`,
      );
      assertSuccess(response, "Failed to mark CDA as viewed");
      return response.data;
    } catch (err: unknown) {
      const status =
        err &&
        typeof err === "object" &&
        "status" in err &&
        typeof err.status === "number"
          ? (err as { status: number }).status
          : undefined;
      if (status === 404 || status === 405) {
        return this.markViewed(id);
      }
      throw err;
    }
  }

  async signRequestViaPublicLink(
    id: string,
    payload: SignCDARequestPayload,
  ): Promise<CDARequest> {
    try {
      const response = await this.httpCoreService.post<
        CDARequest,
        SignCDARequestPayload
      >(`/cda/public/requests/${id}/sign`, payload);
      assertSuccess(response, "Failed to sign CDA");
      return response.data;
    } catch (err: unknown) {
      const status =
        err &&
        typeof err === "object" &&
        "status" in err &&
        typeof err.status === "number"
          ? (err as { status: number }).status
          : undefined;
      // Fall back to authenticated sign only when the public route is unavailable.
      if (status === 404 || status === 405) {
        return this.signRequest(id, payload);
      }
      throw err;
    }
  }

  async rejectRequestViaPublicLink(
    id: string,
    payload: RejectCDARequestPayload,
  ): Promise<CDARequest> {
    try {
      const response = await this.httpCoreService.post<
        CDARequest,
        RejectCDARequestPayload
      >(`/cda/public/requests/${id}/reject`, payload);
      assertSuccess(response, "Failed to reject CDA");
      return response.data;
    } catch (err: unknown) {
      const status =
        err &&
        typeof err === "object" &&
        "status" in err &&
        typeof err.status === "number"
          ? (err as { status: number }).status
          : undefined;
      if (status === 404 || status === 405) {
        return this.rejectRequest(id, payload);
      }
      throw err;
    }
  }

  async requestClarificationViaPublicLink(
    id: string,
    payload: ClarificationCDARequestPayload,
  ): Promise<CDARequest> {
    try {
      const response = await this.httpCoreService.post<
        CDARequest,
        ClarificationCDARequestPayload
      >(`/cda/public/requests/${id}/clarification`, payload);
      assertSuccess(response, "Failed to request clarification");
      return response.data;
    } catch {
      return this.requestClarification(id, payload);
    }
  }

  // ── CDA Templates ─────────────────────────────────────────────────────────

  async listTemplates(
    filters?: CDATemplateFilters,
  ): Promise<CDAListResponse<CDATemplate>> {
    const url = `/cda/templates${this.buildQueryString({
      status: filters?.status,
      region: filters?.region,
      page: filters?.page ?? 1,
      limit: filters?.limit ?? 100,
    })}`;
    const response =
      await this.httpCoreService.get<CDAListResponse<CDATemplate>>(url);
    assertSuccess(response, "Failed to fetch CDA templates");
    return response.data;
  }

  async getTemplateById(id: string): Promise<CDATemplate> {
    const response = await this.httpCoreService.get<CDATemplate>(
      `/cda/templates/${id}`,
    );
    assertSuccess(response, "Failed to fetch CDA template");
    return response.data;
  }

  async createTemplate(
    payload: CreateCDATemplatePayload,
  ): Promise<CDATemplate> {
    const response = await this.httpCoreService.post<
      CDATemplate,
      CreateCDATemplatePayload
    >("/cda/templates", payload);
    assertSuccess(response, "Failed to create CDA template");
    return response.data;
  }

  async updateTemplate(
    id: string,
    payload: UpdateCDATemplatePayload,
  ): Promise<CDATemplate> {
    const response = await this.httpCoreService.patch<
      CDATemplate,
      UpdateCDATemplatePayload
    >(`/cda/templates/${id}`, payload);
    assertSuccess(response, "Failed to update CDA template");
    return response.data;
  }

  async activateTemplate(id: string): Promise<CDATemplate> {
    const response = await this.httpCoreService.post<CDATemplate>(
      `/cda/templates/${id}/activate`,
    );
    assertSuccess(response, "Failed to activate CDA template");
    return response.data;
  }

  async archiveTemplate(id: string): Promise<CDATemplate> {
    const response = await this.httpCoreService.post<CDATemplate>(
      `/cda/templates/${id}/archive`,
    );
    assertSuccess(response, "Failed to archive CDA template");
    return response.data;
  }

  async duplicateTemplate(id: string): Promise<CDATemplate> {
    const response = await this.httpCoreService.post<CDATemplate>(
      `/cda/templates/${id}/duplicate`,
    );
    assertSuccess(response, "Failed to duplicate CDA template");
    return response.data;
  }

  // ── Statistics ────────────────────────────────────────────────────────────

  async getStatistics(studyId?: string): Promise<CDAStatistics> {
    const url = `/cda/statistics${this.buildQueryString({ studyId })}`;
    const response = await this.httpCoreService.get<CDAStatistics>(url);
    assertSuccess(response, "Failed to fetch CDA statistics");
    return response.data;
  }

  // ── Document Blob ──────────────────────────────────────────────────────────

  async getDocumentBlob(id: string): Promise<Blob> {
    // Try the public endpoint first (PI may be unauthenticated); fall back to
    // the authenticated endpoint for logged-in users.
    try {
      return await this.httpCoreService.getBlob(
        `/cda/public/requests/${id}/document`,
      );
    } catch {
      return this.httpCoreService.getBlob(`/cda/requests/${id}/document`);
    }
  }

  async getPublicTemplateByRequestId(
    requestId: string,
  ): Promise<{ id: string; name: string; content: string }> {
    const response = await this.httpCoreService.get<{
      id: string;
      name: string;
      content: string;
    }>(`/cda/public/requests/${requestId}/template`);
    assertSuccess(response, "Failed to fetch CDA template for request");
    return response.data;
  }

  // ── Manual Upload ─────────────────────────────────────────────────────────

  async uploadManualCDA(
    payload: UploadManualCDAPayload,
  ): Promise<ManualCDAUploadResult> {
    const formData = new FormData();
    formData.append("studyId", payload.studyId);
    formData.append("siteId", payload.siteId);
    formData.append("piName", payload.piName);
    formData.append("cdaTitle", payload.cdaTitle);
    formData.append("version", payload.version);
    formData.append("signedDate", payload.signedDate);
    if (payload.dueDate) {
      formData.append("dueDate", payload.dueDate);
    }
    if (payload.comments) {
      formData.append("comments", payload.comments);
    }
    formData.append("signedPdf", payload.signedPdf, payload.signedPdf.name);

    const response = await this.httpCoreService.postForm<ManualCDAUploadResult>(
      "/cda/manual-upload",
      formData,
    );
    assertSuccess(response, "Failed to upload manually signed CDA");
    return response.data;
  }
}
